export interface Viewport {
  scale: number;
  tx: number;
  ty: number;
}

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function boundsFromNodes(nodes: { x: number; y: number }[], pad = 100): Bounds | null {
  if (nodes.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of nodes) {
    minX = Math.min(minX, n.x);
    minY = Math.min(minY, n.y);
    maxX = Math.max(maxX, n.x);
    maxY = Math.max(maxY, n.y);
  }
  return { minX: minX - pad, minY: minY - pad, maxX: maxX + pad, maxY: maxY + pad };
}

/** Fit world bounds into CSS pixel viewport */
export function fitViewport(
  bounds: Bounds,
  width: number,
  height: number,
  maxScale = 1.35,
): Viewport {
  const bw = bounds.maxX - bounds.minX;
  const bh = bounds.maxY - bounds.minY;
  if (bw <= 0 || bh <= 0 || width <= 0 || height <= 0) {
    return { scale: 0.5, tx: width / 2, ty: height / 2 };
  }
  const scale = Math.min(width / bw, height / bh, maxScale);
  const cx = (bounds.minX + bounds.maxX) / 2;
  const cy = (bounds.minY + bounds.maxY) / 2;
  return {
    scale,
    tx: width / 2 - cx * scale,
    ty: height / 2 - cy * scale,
  };
}

export function screenToWorld(
  view: Viewport,
  rect: DOMRect,
  clientX: number,
  clientY: number,
): { x: number; y: number } {
  return {
    x: (clientX - rect.left - view.tx) / view.scale,
    y: (clientY - rect.top - view.ty) / view.scale,
  };
}

export function zoomAtPoint(
  view: Viewport,
  rect: DOMRect,
  clientX: number,
  clientY: number,
  factor: number,
  min = 0.06,
  max = 5,
): Viewport {
  const mx = clientX - rect.left;
  const my = clientY - rect.top;
  const wx = (mx - view.tx) / view.scale;
  const wy = (my - view.ty) / view.scale;
  const next = Math.min(max, Math.max(min, view.scale * factor));
  return {
    scale: next,
    tx: mx - wx * next,
    ty: my - wy * next,
  };
}

export function focusWorldPoint(
  view: Viewport,
  rect: DOMRect,
  wx: number,
  wy: number,
  targetScale?: number,
): Viewport {
  const scale = targetScale ?? Math.max(view.scale, 0.9);
  return {
    scale,
    tx: rect.width / 2 - wx * scale,
    ty: rect.height / 2 - wy * scale,
  };
}